import { roundToCents } from "@common/helpers/monetary";
import { ScheduleApplicationPeriodBase } from "@common/schemas/domains/accounting/schedule.schema";
import {
    ScheduleApplicationPeriodStatus,
    ScheduleJeSubtype,
} from "@common/types/domains/accounting/schedule";
import {
    LiveLadderPeriodInput,
    isLiveLadderPeriod,
} from "./scheduleLadderPeriods";

/**
 * A listed period row. Stored rows (the apply response) go through
 * `withReversedByPeriodId` first.
 */
export type SchedulePeriodSummaryInput = Pick<
    ScheduleApplicationPeriodBase,
    "effectiveDate" | "amount" | "status" | "subtype"
> &
    Pick<LiveLadderPeriodInput, "reversedByPeriodId" | "role"> & {
        milestone?: { sortOrder: number } | null;
    };

function isUnconfirmedMilestone(period: SchedulePeriodSummaryInput): boolean {
    return (
        period.milestone != null &&
        period.status === ScheduleApplicationPeriodStatus.pending
    );
}

export interface SchedulePeriodsSummary {
    termEnd: string | null;
    remainingAmount: number;
}

/**
 * The Schedule column's "Ends {date} · ${left}" figures. "Left" is what the
 * calendar has not yet recognized: the applied amount less every recognition
 * period dated on or before `asOfDate`, whether or not its journal entry has
 * posted (apply posts every unlocked period up front, so posting status says
 * nothing about elapsed time). Voided and reversed periods never count, nor
 * does a milestone still waiting to be confirmed, and neither do reversal or
 * lifecycle rows. The remaining amount mirrors the
 * SQL aggregate the transaction and invoice list endpoints use, so an
 * optimistic cache update after apply matches what the next fetch returns.
 * `termEnd` is the last live month; the list SQL takes the last ladder
 * month, voided or not, so the two differ once a month was voided or
 * reversed.
 */
export function summarizeSchedulePeriods(options: {
    amount: number;
    periods: SchedulePeriodSummaryInput[];
    /** ISO date (YYYY-MM-DD); periods dated on or before it count as recognized. */
    asOfDate: string;
}): SchedulePeriodsSummary {
    const livePeriods = options.periods.filter(isLiveLadderPeriod);
    const termEnd = livePeriods.reduce<string | null>(
        (latest, period) =>
            latest === null || period.effectiveDate > latest
                ? period.effectiveDate
                : latest,
        null,
    );
    const recognizedAmount = livePeriods.reduce(
        (sum, period) =>
            period.subtype === ScheduleJeSubtype.recognized &&
            !isUnconfirmedMilestone(period) &&
            period.effectiveDate <= options.asOfDate
                ? sum + period.amount
                : sum,
        0,
    );
    return {
        termEnd,
        remainingAmount: roundToCents(options.amount - recognizedAmount),
    };
}
